import _extends from "@babel/runtime/helpers/extends";
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoldIcon, WarningBoldIcon } from '@pattern-library/icon';
import cx from 'classnames';
const propTypes = {
  /**
   * Center the label and input in the available horizontal space
   */
  alignCenter: PropTypes.bool,
  /**
   * Class name to add to the container element
   */
  containerClassName: PropTypes.string,
  /**
   * The severity of the error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * error message to display when hasError === true
   **/
  errorMessage: PropTypes.string,
  /**
   * render the component's error state
   **/
  hasError: PropTypes.bool,
  /**
   * Flag to accessibly hide the input label
   */
  hideLabel: PropTypes.bool,
  /**
   * Unique DOM ID and name for the hidden input that holds the entered code
   **/
  id: PropTypes.string.isRequired,
  /**
   * Initial code to display. Must be numeric characters. Focus will automatically be shifted to the last input if defined
   */
  initialCode: PropTypes.string,
  /**
   * Additional classes for the input elements
   */
  inputClass: PropTypes.string,
  /**
   * Flag to indicate whether this input is a required field
   */
  isRequired: PropTypes.bool,
  /**
   * Label
   */
  label: PropTypes.string,
  /**
   * Callback function when the entered code changes. Passes the full code as a string.
   **/
  onChange: PropTypes.func,
  /**
   * Flag to show the asterisk on the label when input is required
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Help text to display below the input
   */
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};
const defaultProps = {
  alignCenter: true,
  containerClassName: '',
  errorLevel: 'error',
  hideLabel: false,
  inputClass: '',
  isRequired: true,
  label: 'Verification Code',
  onChange: () => {},
  showRequiredAsterisk: false
};
const KEY_CODE = {
  BACKSPACE: 8,
  ARROW_LEFT: 37,
  ARROW_RIGHT: 39
};
class OneTimeCode extends Component {
  constructor(props) {
    super(props);
    this.setInitialCode = initialCode => {
      const emptyChars = new Array(6).fill('');
      if (initialCode && /^\d+$/.test(initialCode)) {
        return initialCode.split('').concat(emptyChars).slice(0, 6);
      }
      return emptyChars;
    };
    this.updateCharacter = (value, index) => {
      this.setState(_ref => {
        let {
          characters
        } = _ref;
        return {
          characters: [...characters.slice(0, index), value, ...characters.slice(index + 1)]
        };
      }, () => {
        this.props.onChange(this.state.characters.join(''));
      });
    };
    this.onCharacterChange = (e, index) => {
      const newValue = e.target.value;
      const regex = /^[0-9]?$/;
      if (newValue.match(regex)) {
        this.updateCharacter(newValue, index);
        if (index < this.inputRefs.length - 1 && newValue !== '') {
          this.inputRefs[index + 1].focus();
        }
      }
    };
    this.onPaste = (e, index) => {
      let pastedValue = (e.clipboardData || window.clipboardData).getData('text').substr(0, 6);
      let characters = this.state.characters;
      this.selectTextAfterFocus(0);
      const isOnlyNumbers = /^\d+$/.test(pastedValue);
      if (isOnlyNumbers) {
        if (pastedValue.length === 1) {
          characters[index] = pastedValue;
        } else {
          characters = pastedValue.split('').concat(['', '', '', '', '', '']).splice(0, 6);
        }
      }
      this.setState({
        characters: characters
      }, () => {
        this.props.onChange(this.state.characters.join(''));
      });
      e.preventDefault();
    };
    this.selectTextAfterFocus = index => {
      const inputRef = this.inputRefs[index];
      inputRef.focus();

      // some browsers don't focus on the input if the focus is called immediately
      setTimeout(() => {
        inputRef.setSelectionRange(0, 1);
      }, 0);
    };
    this.onKeydown = (e, index) => {
      if (e.keyCode === KEY_CODE.ARROW_LEFT && index > 0) {
        this.selectTextAfterFocus(index - 1);
      } else if (e.keyCode === KEY_CODE.ARROW_RIGHT && index < this.inputRefs.length - 1) {
        this.selectTextAfterFocus(index + 1);
      } else if (e.keyCode === KEY_CODE.BACKSPACE && index > 0) {
        if (this.inputRefs[index].selectionStart === 0 && this.inputRefs[index].selectionEnd === 0) {
          this.updateCharacter('', index - 1);
          this.inputRefs[index - 1].focus();
        } else {
          this.updateCharacter('', index);
        }
      }
    };
    this.onInput = (e, index) => {
      const value = e.target.value;
      if (value.match(/[^0-9]/)) {
        this.selectTextAfterFocus(index);
      } else if (value !== '') {
        this.selectNextInput(index);
      }
    };
    this.selectNextInput = index => {
      if (index < this.inputRefs.length - 1) {
        this.selectTextAfterFocus(index + 1);
      }
    };
    this.onClick = (e, index) => {
      if (e.target.value !== '') {
        this.selectTextAfterFocus(index);
      }
    };
    this.state = {
      characters: this.setInitialCode(props.initialCode)
    };
    this.inputRefs = [];
    props.onChange(this.state.characters.join(''));
  }
  componentDidMount() {
    const {
      characters
    } = this.state;
    const initialCode = characters.join('');
    if (initialCode.length) {
      const selectionIndex = initialCode.length === 6 ? 5 : characters.indexOf('');
      this.inputRefs[selectionIndex].focus();
      this.inputRefs[selectionIndex].selectionStart = 1;
      this.inputRefs[selectionIndex].selectionEnd = 1;
    }
  }
  render() {
    const {
      alignCenter,
      containerClassName,
      errorLevel,
      errorMessage,
      hasError,
      hideLabel,
      id,
      inputClass,
      label,
      initialCode,
      // eslint-disable-line no-unused-vars
      isRequired,
      onChange,
      // eslint-disable-line no-unused-vars
      showRequiredAsterisk,
      subtext,
      ...inputPassthrough
    } = this.props;
    const {
      characters
    } = this.state;
    const otcClassNames = cx('asc-form-group', 'asc-otc-wrapper', containerClassName, {
      [`asc-form-input--${errorLevel}`]: hasError,
      'asc-otc-wrapper--centered': alignCenter
    });
    const ErrorIcon = errorLevel === 'error' ? ErrorBoldIcon : WarningBoldIcon;
    const ids = {
      help: [`${id}-help`, hasError && Boolean(errorMessage)],
      subtext: [`${id}-subtext`, Boolean(subtext)]
    };
    const ariaIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
    return /*#__PURE__*/React.createElement("div", {
      className: otcClassNames
    }, /*#__PURE__*/React.createElement("p", {
      className: cx('asc-form-label', 'asc-otc-label', {
        'asc-acc-hidden': hideLabel
      })
    }, /*#__PURE__*/React.createElement("span", null, label), isRequired && showRequiredAsterisk && /*#__PURE__*/React.createElement("span", {
      className: "asc-form-required-asterisk"
    }, "\xA0*")), /*#__PURE__*/React.createElement("div", {
      className: "asc-otc-inputs"
    }, characters.map((val, index) => {
      const charId = id + '_' + index;
      const firstCharInputProps = index === 0 ? {
        'aria-describedby': ariaIds.length ? ariaIds.join(' ') : null,
        ...inputPassthrough
      } : {};
      return /*#__PURE__*/React.createElement("div", {
        className: "asc-otc-input",
        key: index
      }, /*#__PURE__*/React.createElement("label", {
        className: "asc-acc-hidden",
        htmlFor: charId
      }, label, " character ", index + 1), /*#__PURE__*/React.createElement("input", _extends({
        className: cx('asc-form-input', inputClass),
        id: charId,
        name: charId,
        value: val,
        onChange: e => this.onCharacterChange(e, index),
        onPaste: e => this.onPaste(e, index),
        onKeyDown: e => this.onKeydown(e, index),
        onInput: e => this.onInput(e, index),
        onClick: e => this.onClick(e, index),
        autoComplete: index === 0 ? 'one-time-code' : 'off',
        inputMode: "numeric",
        pattern: "[0-9]*",
        maxLength: "1",
        required: isRequired,
        ref: input => {
          this.inputRefs[index] = input;
        }
      }, firstCharInputProps)));
    })), /*#__PURE__*/React.createElement("div", {
      className: "asc-otc-sub-input"
    }, hasError && errorMessage && /*#__PURE__*/React.createElement("div", {
      className: "asc-form-help",
      id: ids.help[0]
    }, /*#__PURE__*/React.createElement(ErrorIcon, {
      className: "asc-form-help-icon",
      customSize: "14px",
      color: errorLevel
    }), /*#__PURE__*/React.createElement("p", {
      className: "asc-form-help--error-message"
    }, errorMessage)), subtext && /*#__PURE__*/React.createElement("p", {
      className: "asc-form-subtext",
      id: ids.subtext[0]
    }, subtext)), /*#__PURE__*/React.createElement("input", {
      name: id,
      id: id,
      type: "hidden",
      value: characters.join('')
    }));
  }
}
OneTimeCode.propTypes = propTypes;
OneTimeCode.defaultProps = defaultProps;
export default OneTimeCode;